import React, { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Button, Input, Modal, OtpInput } from '../../../components';
import { Divider } from 'antd';
import { editPhoneNumber, verifyNewPhoneNumber } from 'services';
import { BsFillInfoSquareFill } from 'react-icons/bs';
import { faNumToEnNum } from '../../../../scripts';
import { toast } from 'react-toastify';
import { LoadingOutlined } from '@ant-design/icons';

interface Props {
    open: boolean,
    setOpen: Dispatch<SetStateAction<boolean>>,
    phoneNumber?: string
}

enum Stage {
    EnterPhone,
    EnterCode
}

export const EditPhoneNumberModal: FC<Props> = (props) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [stage, setStage] = useState<Stage>(Stage.EnterPhone);
    const [remainingTime, setRemainingTime] = useState<number>(120);
    const [isTimerActive, setIsTimerActive] = useState<boolean>(true);
    const [resendCodeLoading, setResendCodeLoading] = useState<boolean>(false);

    const maxLength = phoneNumber.startsWith('09') ? 11 : phoneNumber.startsWith('9') ? 10 : 11;

    const isButtonDisabled =
        loading ||
        (phoneNumber.startsWith('9') && phoneNumber.length !== 10) ||
        (phoneNumber.startsWith('09') && phoneNumber.length !== 11) ||
        (!phoneNumber.startsWith('9') && phoneNumber.length !== 11);

    const handleResendClick = () => {
        let formattedPhoneNumber = phoneNumber;

        if (phoneNumber.startsWith('9') && phoneNumber.length === 10) {
            formattedPhoneNumber = '0' + phoneNumber;
        }

        if (formattedPhoneNumber.length === 11) {
            const data = { newPhoneNumber: formattedPhoneNumber };
            const onComplete = () => {
                setCode('');
                setRemainingTime(120);
                setIsTimerActive(true);
            };

            editPhoneNumber(setResendCodeLoading, data, onComplete);
        }
    };

    const handleSubmit = () => {
        let formattedPhoneNumber = phoneNumber;

        if (phoneNumber.startsWith('9') && phoneNumber.length === 10) {
            formattedPhoneNumber = '0' + phoneNumber;
        }

        if (formattedPhoneNumber.length === 11) {
            const data = { newPhoneNumber: formattedPhoneNumber };
            const onComplete = () => {
                setStage(Stage.EnterCode)
                setRemainingTime(120)
            };

            editPhoneNumber(setLoading, data, onComplete);
        }
    };

    const handleCodeSubmit = () => {
        const onComplete = () => {
            toast.success('شماره موبایل با موفقیت ویرایش شد.')
        }

        verifyNewPhoneNumber(setLoading, { otpOldPhoneNumber: code, newPhoneNumber: phoneNumber}, onComplete)
    }

    const renderTitle = () => {
        switch (stage) {
            case Stage.EnterPhone: return 'ویرایش شماره موبایل';
            case Stage.EnterCode: return 'تایید شماره موبایل'
            default: return ''
        }
    }

    useEffect(() => {
        if (!props.open) {
            setStage(Stage.EnterPhone);
            setPhoneNumber('')
            setCode('')
        }
    }, [props.open]);

    useEffect(() => {
        if (isTimerActive && remainingTime > 0) {
            const timer = setInterval(() => {
                setRemainingTime((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        } else if (remainingTime === 0) {
            setIsTimerActive(false);
        }
    }, [isTimerActive, remainingTime]);

    return (
        <Modal open={props.open} setOpen={props.setOpen} title={renderTitle()}>
            <Divider />
            {stage === Stage.EnterPhone && (
                <>
                    <Input
                        label={'شماره موبایل جدید:'}
                        maxLength={maxLength}
                        className={'mt-2'}
                        placeholder={'مثال : 09123456789'}
                        value={phoneNumber}
                        dir={phoneNumber ? 'ltr' : 'rtl'}
                        onChange={(e) => setPhoneNumber(faNumToEnNum(e.target.value))}
                        onPressEnter={handleSubmit}
                        inputMode={'numeric'}
                    />
                    <p className={'flex items-center gap-2 text-sm mt-4 font-normal'}>
                        <BsFillInfoSquareFill className={'text-warning-light'} />
                        شماره موبایل باید به نام خودتان باشد!
                    </p>
                    <p className={'flex items-center gap-2 text-sm mt-4 font-normal'}>
                        <BsFillInfoSquareFill className={'text-warning-light'} />
                        برای امنیت بیشتر، پس از تغییر شماره تا 24 ساعت، امکان برداشت ارزی و ریالی وجود ندارد.
                    </p>
                    <div className={'flex items-center gap-4 mt-6'}>
                        <Button type={'primary'} className={'w-full'} loading={loading} disabled={isButtonDisabled}
                                onClick={handleSubmit}>تایید</Button>
                        <Button type={'text'} className={'bg-background w-full'}
                                onClick={() => props.setOpen(false)}>انصراف</Button>
                    </div>
                </>
            )}
            {stage === Stage.EnterCode && (
                <>
                    <span className={'flex text-sm font-medium mt-6'}>
                         کد پیامک شده به شماره {props?.phoneNumber} را وارد کنید
                    </span>
                    <div className={'flex items-center justify-between mt-4'}>
                        <span className={'text-base font-bold'}>کد پیامک شده:</span>
                        {remainingTime === 0 ? (
                                <p className={`flex text-base items-center gap-2 font-bold clickable ${resendCodeLoading ? '!opacity-50' : ''}`}
                                   onClick={handleResendClick}>
                                    ارسال مجدد کد
                                    {resendCodeLoading ? <LoadingOutlined /> : null}
                                </p>
                            ) :
                            <p className={'font-medium'}>
                                <span className={'font-bold'}>{remainingTime} ثانیه </span>تا ارسال مجدد کد
                            </p>
                        }
                    </div>
                    <OtpInput className={"mt-2"} length={6} onChange={(code) => setCode(code)} />
                    <div className={'flex items-center gap-4 mt-6'}>
                        <Button type={'primary'} className={'w-full'} loading={loading} disabled={loading || code.length !== 6}
                                onClick={handleCodeSubmit}>تایید کد</Button>
                        <Button type={'text'} className={'bg-background w-full'}
                                onClick={() => {
                                    setStage(Stage.EnterPhone);
                                }}>ویرایش شماره موبایل</Button>
                    </div>
                </>
            )}
        </Modal>
    );
};